import { ACADEMIC_STATUS_MAP } from 'constants/academic-status-map';
import { IClassroomsEnrollmentsData } from 'services/graduate-enrollment/evaluations';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ReactNode, useMemo } from 'react';
import { Tag, Tooltip, Typography } from 'antd';

interface ClasssroomEnrollmentHeaderProps {
  classroomEnrollment: IClassroomsEnrollmentsData;
}

const MetricField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex min-w-0 flex-col">
    <Typography.Text type="secondary" className="text-xs leading-none">
      {label}
    </Typography.Text>
    <Typography.Text className="mt-1 text-sm text-neutral-800">
      {value}
    </Typography.Text>
  </div>
);

export const ClasssroomEnrollmentHeader = ({
  classroomEnrollment
}: ClasssroomEnrollmentHeaderProps) => {
  const status = useMemo(() => {
    if (classroomEnrollment.academic_record_status) {
      return ACADEMIC_STATUS_MAP[classroomEnrollment.academic_record_status];
    }

    return {
      color: 'blue',
      label: 'Em andamento'
    };
  }, [classroomEnrollment.academic_record_status]);
  const hasAcademicRecordEditTooltip = (() => {
    const { approved, academic_record_status } = classroomEnrollment;

    const isApprovedByAcademicRecord =
      !approved && academic_record_status === 'approved';

    const isStillApprovedButAcademicRecordFailed =
      approved &&
      (academic_record_status === 'failed' ||
        academic_record_status === 'attendance_failed');

    return isApprovedByAcademicRecord || isStillApprovedButAcademicRecordFailed;
  })();

  const originalStatusLabel = classroomEnrollment.approved
    ? 'Aprovado'
    : 'Reprovado';
  const previousScoreLabel = classroomEnrollment.score ?? '-';
  const attendanceValue =
    classroomEnrollment.academic_record?.attendance_rate ??
    classroomEnrollment.academic_record_attendance_rate ??
    classroomEnrollment.attendance_rate;
  const attendanceRate = attendanceValue != null ? `${attendanceValue}%` : '-';
  const progressPercentage =
    classroomEnrollment.progress_percentage != null
      ? `${classroomEnrollment.progress_percentage}%`
      : '-';
  const score = classroomEnrollment.academic_record_score ?? '-';

  return (
    <div className="flex w-full items-center justify-between gap-6">
      <div className="min-w-0 flex-1">
        <Typography.Text strong className="block truncate text-neutral-800">
          {classroomEnrollment.classroom.discipline.name}
        </Typography.Text>
        <Typography.Text type="secondary" className="block text-sm">
          Turma: {classroomEnrollment.classroom.code}
        </Typography.Text>
      </div>

      <div className="grid shrink-0 grid-cols-[4.5rem_4.5rem_3.25rem_11rem] items-center gap-x-8">
        <MetricField label="Frequência" value={attendanceRate} />
        <MetricField label="Progresso" value={progressPercentage} />
        <MetricField label="Nota" value={score} />
        <div className="flex items-center">
          <Tag color={status.color} className="m-0 whitespace-nowrap">
            {status.label}
            {hasAcademicRecordEditTooltip && (
              <Tooltip
                title={
                  <span>
                    Edição no histórico acadêmico. Status anterior:{' '}
                    {originalStatusLabel}
                    <br />
                    Nota anterior: {previousScoreLabel}
                  </span>
                }
              >
                <InfoCircleOutlined className="ml-1 text-xs text-blue-500 align-middle" />
              </Tooltip>
            )}
          </Tag>
        </div>
      </div>
    </div>
  );
};
